import { Badge, Box, Button, Text, VStack } from '@chakra-ui/react'
import type { CaseParty } from '@/lib/services/caseThreadsService'
import { queueLabel } from '@/lib/utils/caseThreads'

type Props = {
  parties: CaseParty[]
  selectedId: string | null
  unlinkedCount: number
  needsReviewCount: number
  activeQueue: 'unlinked' | 'needs_review' | null
  onSelectParty: (id: string) => void
  onSelectQueue: (queue: 'unlinked' | 'needs_review') => void
}

export function PartyList({
  parties,
  selectedId,
  unlinkedCount,
  needsReviewCount,
  activeQueue,
  onSelectParty,
  onSelectQueue,
}: Props) {
  return (
    <VStack align="stretch" spacing={1} p={2} overflowY="auto" h="100%">
      <QueueRow
        label={queueLabel('unlinked')}
        count={unlinkedCount}
        active={activeQueue === 'unlinked'}
        onClick={() => onSelectQueue('unlinked')}
      />
      <QueueRow
        label={queueLabel('needs_review')}
        count={needsReviewCount}
        active={activeQueue === 'needs_review'}
        onClick={() => onSelectQueue('needs_review')}
      />
      {parties.length === 0 ? (
        <Text fontSize="sm" color="gray.500" px={2} py={4}>
          No clients or requestors with ticket requests yet.
        </Text>
      ) : (
        parties.map((party) => (
          <Button
            key={party.id}
            variant={selectedId === party.id && !activeQueue ? 'solid' : 'ghost'}
            justifyContent="space-between"
            size="sm"
            onClick={() => onSelectParty(party.id)}
          >
            <Text noOfLines={1} textAlign="left">
              {party.label}
            </Text>
            <Box>
              {(party.unread_count ?? 0) > 0 ? (
                <Badge mr={1} colorScheme="orange">
                  {party.unread_count} new
                </Badge>
              ) : null}
              <Badge>{party.request_count}</Badge>
            </Box>
          </Button>
        ))
      )}
    </VStack>
  )
}

function QueueRow({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <Button variant={active ? 'solid' : 'outline'} colorScheme={active ? 'orange' : 'gray'} size="sm" justifyContent="space-between" onClick={onClick}>
      <Box as="span">{label}</Box>
      <Badge colorScheme="orange">{count}</Badge>
    </Button>
  )
}
